// Course Package Import — runner (Step 3: course + topics only).
// Browser-only orchestration over EXISTING endpoints. No new backend routes.
// Steps 4–5 add blocks, files, Add dedupe and Replace. Per PRD §6/§7/§8.

import api from '../../api/client';
import { uploadAssets } from './packageUpload';
import { createBlocks } from './packageBlocks';

const FORK_REFUSED_MSG = 'Import into the template course; forks get changes from their template.';

// imp_<YYYYMMDD>_<HHmmss>_<6 base36> (UTC) — PRD §7.3
export function makeBatchId(d = new Date()) {
  const p = (n, w = 2) => String(n).padStart(w, '0');
  const ymd = `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}`;
  const hms = `${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}`;
  const rand = Math.random().toString(36).slice(2, 8).padEnd(6, '0');
  return `imp_${ymd}_${hms}_${rand}`;
}

const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
const errText = (e) => e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || 'request failed';

// Course fields allowed through to PUT /courses/{id} (CourseUpdate) — PRD §5.5
const COURSE_FIELD_KEYS = ['shortTitle', 'showShortTitleOverlay', 'sequentialUnlock', 'certificateEnabled', 'xpReward', 'lockdownSettings', 'proctoringSettings', 'scheduledPublishAt'];
// Topic fields allowed through to PUT /topics/{id} (TopicUpdate)
const TOPIC_FIELD_KEYS = ['skillIds', 'xpReward', 'scheduledPublishAt'];

/**
 * @param {object} opts
 *   api? (defaults to shared client), orgId, mode ('new'|'add'|'replace'),
 *   targetCourseId (add/replace), readResult, plan, onProgress(fn)
 * @returns {Promise<Report>}
 */
export async function runImport(opts) {
  const { orgId, mode, targetCourseId, readResult, plan } = opts;
  const client = opts.api || api;
  const onProgress = opts.onProgress || (() => {});
  const onItem = opts.onItem || (() => {});
  const emit = (group, it) => { try { onItem({ group, ...it }); } catch (e) { /* ignore */ } };
  const index = readResult.index || {};
  const batchId = makeBatchId();

  const report = {
    ok: false,
    batchId,
    mode,
    orgId,
    courseId: null,
    packageName: readResult.packageName,
    course: { status: 'pending', message: '' },
    topics: [],
    blocks: [],
    uploads: [],
    assets: null,
    ledger: opts.ledger || {},
    summary: { topicsCreated: 0, topicsMatched: 0, topicsFailed: 0, blocksCreated: 0, blocksSkipped: 0, blocksNotCreated: 0, blocksFailed: 0, filesUploaded: 0, filesSkipped: 0, filesFailed: 0, filesRefused: 0 },
    forkCount: 0,
    templateVersionBefore: null,
    templateVersionAfter: null,
    aborted: null,
  };

  // ---- resolve / create the TEMPLATE course ----
  let courseId = null;
  try {
    if (mode === 'new') {
      courseId = await createCourse(client, orgId, index.course || {});
      report.course.status = 'created';
      report.course.message = 'New template course created';
    } else {
      // add / replace — target must be an existing TEMPLATE in this org
      const course = await getCourse(client, orgId, targetCourseId);
      if (!course) { report.aborted = 'Target course not found in this organization.'; return finish(report); }
      if (course.kind === 'fork' || course.forkedFromCourseId) {
        report.aborted = FORK_REFUSED_MSG; return finish(report);
      }
      courseId = course.id;
      report.course.status = 'matched';
      report.course.message = `Target template: ${course.title}`;
      if (mode === 'replace') {
        if (course.isPublished) { report.aborted = 'Unpublish the course first, or use Add. Replace only works on a draft.'; report.courseId = courseId; return finish(report); }
        if ((opts.confirmTitle || '').trim() !== String(course.title).trim()) { report.aborted = 'Type the exact course title to confirm Replace.'; report.courseId = courseId; return finish(report); }
        // count ALL forks of this template (draft + published), not the
        // lifecycle-filtered course list. sync/preview returns every fork.
        try {
          const fr = await client.get(`/organizations/${orgId}/courses/${courseId}/sync/preview`);
          report.forkCount = (fr.data?.forks || []).length;
        } catch (e) { report.forkCount = 0; }
        // delete existing topics (deletes their blocks too); carry on if one fails
        try {
          const tr = await client.get(`/organizations/${orgId}/courses/${courseId}/topics`);
          for (const old of (tr.data || [])) {
            try { await client.delete(`/organizations/${orgId}/topics/${old.id}`); }
            catch (e) { report.topics.push({ key: `old:${old.id}`, name: old.name, status: 'error', message: `could not delete: ${errText(e)}`, topicId: old.id }); }
          }
        } catch (e) { report.aborted = `Could not read existing topics: ${errText(e)}`; report.courseId = courseId; return finish(report); }
        report.course.message = `Replaced content of template: ${course.title} (${report.forkCount || 0} fork(s) keep their content until synced)`;
      }
    }
  } catch (e) {
    report.course.status = 'error';
    report.course.message = errText(e);
    report.aborted = `Could not prepare the course: ${errText(e)}`;
    return finish(report);
  }
  report.courseId = courseId;

  // ---- template version BEFORE the import's structural edits ----
  try {
    const c0 = await client.get(`/organizations/${orgId}/courses/${courseId}`);
    report.templateVersionBefore = c0.data?.version ?? null;
  } catch (e) { /* leave null */ }

  // ---- course fields ----
  try {
    const patch = buildCourseFieldPatch(index.course || {});
    if (Object.keys(patch).length) {
      await client.put(`/organizations/${orgId}/courses/${courseId}`, patch);
    }
  } catch (e) {
    report.course.message += ` (some course fields skipped: ${errText(e)})`;
  }

  // ---- existing topics for Add matching ----
  let existingTopics = [];
  if (mode === 'add') {
    try {
      const r = await client.get(`/organizations/${orgId}/courses/${courseId}/topics`);
      existingTopics = r.data || [];
    } catch (e) { /* treat as none */ }
  }

  // ---- topics ----
  const topicIdByKey = new Map();
  const pkgTopics = Array.isArray(index.topics) ? index.topics : [];
  let i = 0;
  for (const t of pkgTopics) {
    i += 1;
    onProgress({ phase: 'topics', current: i, total: pkgTopics.length, name: t.name });
    const planTopic = (plan?.topics || []).find((p) => p.key === t.key);
    const tReport = { key: t.key, name: t.name, status: 'pending', message: '', topicId: null, matched: false };

    // skip topics the validator already flagged as error (bad name/key/placeholder)
    if (planTopic && planTopic.status === 'error') {
      tReport.status = 'skipped';
      tReport.message = 'Skipped — fix the errors shown in preview';
      report.topics.push(tReport);
      emit('topic', tReport);
      continue;
    }

    try {
      let topicId = null;
      const match = mode === 'add'
        ? existingTopics.find((et) => norm(et.name) === norm(t.name))
        : null;
      if (match) {
        topicId = match.id;
        tReport.matched = true;
        tReport.status = 'matched';
        tReport.message = `Matched existing topic "${match.name}"`;
        report.summary.topicsMatched += 1;
      } else {
        const created = await client.post(`/organizations/${orgId}/courses/${courseId}/topics`, {
          name: t.name,
          summary: t.summary || null,
          pedagogy: Array.isArray(t.pedagogy) ? t.pedagogy : [],
        });
        topicId = created.data.id;
        tReport.status = 'created';
        tReport.message = 'Topic created';
        report.summary.topicsCreated += 1;
      }
      tReport.topicId = topicId;
      topicIdByKey.set(t.key, topicId);

      // topic fields via PUT (skillIds / xpReward / scheduledPublishAt)
      const tPatch = buildTopicFieldPatch(t.fields || {});
      if (Object.keys(tPatch).length) {
        try { await client.put(`/organizations/${orgId}/topics/${topicId}`, tPatch); }
        catch (e) { tReport.message += ` (some topic fields skipped: ${errText(e)})`; }
      }
    } catch (e) {
      tReport.status = 'error';
      tReport.message = errText(e);
      report.summary.topicsFailed += 1;
    }
    report.topics.push(tReport);
    emit('topic', tReport);
  }

  // ---- Step 4: upload the files the package references (no blocks yet) ----
  try {
    onProgress({ phase: 'files', current: 0, total: 0, name: 'uploading files' });
    // PRD §3: before reusing a recorded upload, check it still exists.
    const checkExists = async (desc) => {
      try {
        if (desc.datasetId) { await client.get(`/orgs/${orgId}/datasets/${desc.datasetId}`); return true; }
        if (desc.url) { await client.get(desc.url); return true; }
        return true;
      } catch (e) { return false; }
    };
    const { assets, uploads, ledger } = await uploadAssets({
      api: client, orgId, readResult, plan, ledger: report.ledger, onProgress, checkExists,
    });
    report.assets = assets;
    report.uploads = uploads;
    report.ledger = ledger;
    for (const u of uploads) {
      if (u.status === 'uploaded') report.summary.filesUploaded += 1;
      else if (u.status === 'skipped') report.summary.filesSkipped += 1;
      else if (u.status === 'failed') report.summary.filesFailed += 1;
      else if (u.status === 'refused') report.summary.filesRefused += 1;
      emit('upload', u);
    }
    // apply thumbnail to the course record (single upload, both modes)
    if (assets.thumbnailUrl && courseId) {
      try { await client.put(`/organizations/${orgId}/courses/${courseId}`, { thumbnailUrl: assets.thumbnailUrl }); }
      catch (e) { report.course.message += ` (thumbnail not linked: ${errText(e)})`; }
    }
  } catch (e) {
    report.uploads.push({ key: '-', kind: 'files', status: 'failed', message: errText(e) });
  }

  // ---- Step 5: create the blocks (links the uploads above) ----
  try {
    // existing importKeys in the course → rerun dedupe
    const existingImportKeys = new Set();
    try {
      const tr = await client.get(`/organizations/${orgId}/courses/${courseId}/topics`);
      for (const tp of (tr.data || [])) {
        const br = await client.get(`/organizations/${orgId}/topics/${tp.id}/content-blocks`);
        for (const bl of (br.data || [])) { const k = bl?.content?.importKey; if (k) existingImportKeys.add(k); }
      }
    } catch (e) { /* treat as none */ }

    const blocks = await createBlocks({
      api: client, orgId, courseId, readResult, plan, assets: report.assets || { blocks: {} },
      batchId, pkg: readResult.packageName, topicIdByKey, existingImportKeys, onProgress, onItem,
    });
    report.blocks = blocks;
    for (const b of blocks) {
      if (b.status === 'created') report.summary.blocksCreated += 1;
      else if (b.status === 'skipped') report.summary.blocksSkipped += 1;
      else if (b.status === 'not-created') report.summary.blocksNotCreated += 1;
      else if (b.status === 'error') report.summary.blocksFailed += 1;
    }
  } catch (e) {
    report.blocks.push({ key: '-', type: '-', status: 'error', message: errText(e) });
  }

  // ---- template version AFTER the import's structural edits ----
  try {
    const c1 = await client.get(`/organizations/${orgId}/courses/${courseId}`);
    report.templateVersionAfter = c1.data?.version ?? null;
  } catch (e) { /* leave null */ }

  return finish(report);
}

function finish(report) {
  const hasTopicError = report.topics.some((t) => t.status === 'error');
  report.ok = !report.aborted && report.course.status !== 'error' && !hasTopicError;
  return report;
}

// ---- endpoint helpers ----
async function createCourse(client, orgId, c) {
  const body = {
    course: {
      title: c.title,
      description: c.description || null,
      learningObjectives: c.learningObjectives || [],
      targetAudience: c.targetAudience || null,
      prerequisites: c.prerequisites || [],
      duration: c.duration || null,
      difficulty: ['Beginner', 'Intermediate', 'Advanced'].includes(c.difficulty) ? c.difficulty : 'Beginner',
      domain: c.domain || null,
      // IMPORTANT: no topics here — topics are created uniformly below so the
      // template version bumps and fields are applied consistently.
    },
  };
  const r = await client.post(`/organizations/${orgId}/courses/import`, body);
  return r.data.courseId;
}

async function getCourse(client, orgId, courseId) {
  if (!courseId) return null;
  try {
    const r = await client.get(`/organizations/${orgId}/courses/${courseId}`);
    return r.data;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}

function buildCourseFieldPatch(c) {
  const patch = {};
  const fields = c.fields || {};
  for (const k of COURSE_FIELD_KEYS) {
    if (fields[k] !== undefined && !String(fields[k]).includes?.('{{FILL:')) patch[k] = fields[k];
  }
  return patch;
}

function buildTopicFieldPatch(fields) {
  const patch = {};
  for (const k of TOPIC_FIELD_KEYS) {
    if (fields[k] !== undefined) patch[k] = fields[k];
  }
  return patch;
}
